import React, { useEffect, useState } from 'react';
import {
    AppBar,
    Box,
    Toolbar,
    IconButton,
    Typography,
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    useTheme,
    useMediaQuery,
    Avatar,
    Paper,
    Fade,
    Tooltip,
    ListItemButton,
    Menu,
    MenuItem
} from '@mui/material';
import { useLanguage } from './LanguageContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import LogoutConfirmation from './LogoutConfirmation';
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";
import SchoolIcon from '@mui/icons-material/School';
import QuizIcon from '@mui/icons-material/Quiz';
import AssignmentIcon from '@mui/icons-material/Assignment';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import LanguageIcon from '@mui/icons-material/Language';

const drawerWidth = 260;

const Layout = ({ children }) => {
    const [activeSection, setActiveSection] = useState('my-class');
    const [mobileOpen, setMobileOpen] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [user, setUser] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const theme = useTheme();
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const { language, setLanguage, t } = useLanguage();
    const location = useLocation();
    const navigate = useNavigate();
    const auth = getAuth();

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
        });
        return () => unsubscribe();
    }, [auth]);

    useEffect(() => {
        const path = location.pathname;
        let title = `${t.dashboard} | ClassCapture`;

        if (path === '/my-results') {
            setActiveSection('my-results');
            title = `${t.myResults} | ClassCapture`;
        } else if (path === '/take-test') {
            setActiveSection('take-test');
            title = `${t.takeTest} | ClassCapture`;
        } else if (path === '/my-class') {
            setActiveSection('my-class');
            title = `${t.classroom} | ClassCapture`;
        }
        document.title = title;
    }, [location, language, t]);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate("/");
        } catch (error) {
            console.error("Error signing out:", error);
        }
    };

    const handleLanguageMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleLanguageMenuClose = () => {
        setAnchorEl(null);
    };

    const changeLanguage = (lang) => {
        setLanguage(lang);
        handleLanguageMenuClose();
    };

    // Gradient background style
    const gradientBackground = {
        background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
    };

    const navItems = [
        {
            key: 'my-class',
            label: t.classroom,
            icon: <SchoolIcon />,
            path: '/my-class',
            description: t.classroomDescription
        },
        {
            key: 'take-test',
            label: t.takeTest,
            icon: <QuizIcon />,
            path: '/take-test',
            description: t.testDescription
        },
        {
            key: 'my-results',
            label: t.myResults,
            icon: <AssignmentIcon />,
            path: '/my-results',
            description: t.resultsDescription
        },
    ];

    const drawer = (
        <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            <Fade in={true} timeout={800}>
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        ...gradientBackground,
                        color: 'white',
                        borderRadius: 0
                    }}
                >
                </Paper>
            </Fade>

            <Box sx={{
                flexGrow: 1,
                overflow: 'auto',
                px: 2,
                py: 3
            }}>
                <List component="nav" sx={{ px: 0 }}>
                    {navItems.map((item) => (
                        <Tooltip
                            key={item.key}
                            title={item.description}
                            placement="right"
                            arrow
                        >
                            <ListItem
                                disablePadding
                                sx={{
                                    mb: 1,
                                    borderRadius: 1,
                                    overflow: 'hidden'
                                }}
                            >
                                <ListItemButton
                                    component={Link}
                                    to={item.path}
                                    selected={activeSection === item.key}
                                    onClick={() => {
                                        setActiveSection(item.key);
                                        if (mobileOpen) setMobileOpen(false);
                                    }}
                                    sx={{
                                        borderRadius: 1,
                                        py: 1.2,
                                        '&.Mui-selected': {
                                            bgcolor: `${theme.palette.primary.light}20`,
                                            color: theme.palette.primary.main,
                                            '&:hover': {
                                                bgcolor: `${theme.palette.primary.light}30`,
                                            },
                                            '& .MuiListItemIcon-root': {
                                                color: theme.palette.primary.main,
                                            }
                                        },
                                        '&:hover': {
                                            bgcolor: theme.palette.action.hover,
                                        }
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={item.label}
                                        primaryTypographyProps={{
                                            fontWeight: activeSection === item.key ? 450 : 100
                                        }}
                                    />
                                    {activeSection === item.key && (
                                        <Box
                                            sx={{
                                                width: 4,
                                                height: 32,
                                                bgcolor: theme.palette.primary.main,
                                                borderRadius: 1,
                                                ml: 1
                                            }}
                                        />
                                    )}
                                </ListItemButton>
                            </ListItem>
                        </Tooltip>
                    ))}
                </List>
            </Box>

            {/* Logo de l'app centré sous le bouton */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 5 }}>
                <Box component="img" src="/png/classcapture.png" alt="ClassCapture Logo" sx={{ width: 100, height:'auto' }} />
            </Box>

            {/* Suppression du bouton de déconnexion du drawer */}
        </Box>
    );

    return (
        <Box sx={{ display: 'flex' }}>
            <LogoutConfirmation
                open={showConfirm}
                onConfirm={handleLogout}
                onCancel={() => setShowConfirm(false)}
                language={language}
            />

            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    ...gradientBackground,
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { sm: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {isTablet && (
                            <Avatar
                                sx={{
                                    bgcolor: 'white',
                                    color: theme.palette.primary.main,
                                    width: 32,
                                    height: 32,
                                    mr: 1.5,
                                    display: { xs: 'flex', sm: 'none' }
                                }}
                            >
                                <SchoolIcon fontSize="small" />
                            </Avatar>
                        )}

                        <Typography
                            variant="h6"
                            noWrap
                            component="div"
                            fontWeight={450}
                            color="black"
                        >
                            {user ? `${t.hello} ${user.displayName || t.user} !` : "ClassCapture"}
                        </Typography>

                    </Box>

                    <Box sx={{ flexGrow: 1 }} />

                    {/* Bouton de sélection de langue */}
                    <IconButton
                        color="gray"
                        onClick={handleLanguageMenuOpen}
                        sx={{ mr: 2 }}
                    >
                        <LanguageIcon />
                        <Typography variant="body2" sx={{ ml: 1 }}>
                            {language.toUpperCase()}
                        </Typography>
                    </IconButton>

                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleLanguageMenuClose}
                    >
                        <MenuItem onClick={() => changeLanguage('en')} selected={language === 'en'}>
                            English (EN)
                        </MenuItem>
                        <MenuItem onClick={() => changeLanguage('fr')} selected={language === 'fr'}>
                            Français (FR)
                        </MenuItem>
                    </Menu>

                    {/* Bouton de déconnexion */}
                    <Tooltip title={t.logout}>
                        <IconButton
                            color="error"
                            onClick={() => setShowConfirm(true)}
                            sx={{ mr: 1 }}
                        >
                            <LogoutIcon />
                        </IconButton>
                    </Tooltip>

                    {user && isTablet && (
                        <Tooltip title={user.email || t.user}>
                            <Avatar
                                src={user.photoURL}
                                alt={user.displayName}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    cursor: 'pointer',
                                    border: '2px solid rgba(255,255,255,0.5)'
                                }}
                            >
                                {user.displayName ? user.displayName.charAt(0).toUpperCase() : t.user.charAt(0)}
                            </Avatar>
                        </Tooltip>
                    )}
                </Toolbar>
            </AppBar>

            {/* Drawer pour mobile */}
            <Box
                component="nav"
                sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
                aria-label="navigation links"
            >
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true,
                    }}
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: drawerWidth,
                            boxShadow: '0 0 15px rgba(0,0,0,0.1)'
                        },
                    }}
                >
                    {drawer}
                </Drawer>

                {/* Drawer permanent pour écrans larges */}
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: drawerWidth,
                            border: 'none',
                            boxShadow: '0 0 15px rgba(0,0,0,0.05)'
                        },
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    bgcolor: theme.palette.background.default,
                    minHeight: '100vh'
                }}
            >
                <Toolbar />
                <Fade in={true} timeout={450}>
                    <Box>
                        {children}
                    </Box>
                </Fade>
            </Box>
        </Box>
    );
};

export default Layout;